import { TextArea } from "@carbon/react";
import get from "lodash.get";
import {
  Control,
  Controller,
  FieldPath,
  FieldValues,
  RegisterOptions,
  useFormState,
} from "react-hook-form";

export type TextAreaFormFieldProps<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>
> = {
  id: string;
  label: string;
  type?: string;
  helperText?: string;
  placeholder?: string;
  readonly?: boolean;
  control: Control<TFieldValues>;
  ["aria-label"]?: string;
  rules?: Omit<
    RegisterOptions<TFieldValues, TName>,
    "valueAsNumber" | "valueAsDate" | "setValueAs" | "disabled"
  >;
  name: TName;
};

export function TextAreaFormField<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>
>({
  id,
  name,
  type,
  label,
  placeholder,
  control,
  helperText,
  readonly,
  rules,
  "aria-label": ariaLabel,
}: TextAreaFormFieldProps<TFieldValues, TName>) {
  const { errors } = useFormState({ control });
  const error = get(errors, name);

  return (
    <Controller
      name={name}
      control={control}
      rules={rules}
      render={({ field }) => (
        <TextArea
          id={id}
          labelText={label}
          type={type}
          readOnly={readonly}
          placeholder={placeholder}
          invalidText={typeof error?.message === "string" ? error.message : null}
          invalid={!!error?.message}
          helperText={helperText}
          aria-label={ariaLabel}
          {...field}
        />
      )}
    />
  );
}
